import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  BookOpen,
  Loader2,
  Plus,
  Trash2,
  Edit3,
  Check,
  X,
  ThumbsUp,
  ThumbsDown,
  Sparkles,
  RefreshCw,
  Save,
} from 'lucide-react';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { Input } from '../ui/input';
import { Switch } from '../ui/switch';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../ui/tabs';

const BACKEND = process.env.REACT_APP_BACKEND_URL || '';

const authHeaders = () => {
  const t = localStorage.getItem('le_token');
  return t ? { Authorization: `Bearer ${t}` } : {};
};

const KIND_META = {
  rule: {
    label: 'Style Rules',
    hint: 'Hard directives every post must obey — "Never open with In my X years", "Always end with a question", etc.',
    placeholder: 'One rule per row. Keep it short and imperative.',
    testid: 'trainer-tab-rules',
    color: 'bg-primary/10 text-primary border-primary/20',
  },
  exemplar_good: {
    label: 'Good Exemplars',
    hint: 'Full posts the writer should learn to imitate — voice, rhythm, structure, moves.',
    placeholder: 'Paste one full LinkedIn post the model should learn from.',
    testid: 'trainer-tab-good',
    color: 'bg-emerald-500/10 text-emerald-600 border-emerald-500/20',
  },
  exemplar_bad: {
    label: 'Bad Exemplars',
    hint: 'Posts the writer must NEVER imitate — anti-patterns, worn-out hooks, AI-slop tells.',
    placeholder: 'Paste a full post the model should avoid imitating.',
    testid: 'trainer-tab-bad',
    color: 'bg-rose-500/10 text-rose-600 border-rose-500/20',
  },
};

/**
 * PostWriterTrainerPanel — admin CRM tab that lets you curate the
 * house style for the post-generation LLM.
 *
 * Two persistent surfaces:
 *   1. Training corpus (Rules + Good/Bad exemplars) with CRUD.
 *   2. Live feedback digest — top-rated / bottom-rated posts from the
 *      user thumbs loop. Auto-promotion happens server-side; this
 *      panel just surfaces the aggregate so admins see what the
 *      buyers are actually loving vs rejecting.
 */
export function PostWriterTrainerPanel() {
  const [activeTab, setActiveTab] = useState('rule');
  const [rowsByKind, setRowsByKind] = useState({ rule: [], exemplar_good: [], exemplar_bad: [] });
  const [loading, setLoading] = useState(false);
  const [summary, setSummary] = useState(null);
  const [draft, setDraft] = useState({ text: '', label: '' });
  const [editingId, setEditingId] = useState(null);
  const [editText, setEditText] = useState('');

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${BACKEND}/api/admin/post-writer/training`, {
        headers: authHeaders(),
        timeout: 20000,
      });
      const grouped = { rule: [], exemplar_good: [], exemplar_bad: [] };
      for (const r of data?.items || []) {
        if (r?.kind && grouped[r.kind]) grouped[r.kind].push(r);
      }
      setRowsByKind(grouped);
    } catch (e) {
      toast.error('Could not load training corpus.');
    } finally {
      setLoading(false);
    }
  }, []);

  const loadSummary = useCallback(async () => {
    try {
      const { data } = await axios.get(
        `${BACKEND}/api/admin/post-writer/feedback-summary`,
        { headers: authHeaders(), timeout: 20000 },
      );
      setSummary(data);
    } catch (e) {
      // Silent — feedback summary is optional.
    }
  }, []);

  useEffect(() => {
    loadAll();
    loadSummary();
  }, [loadAll, loadSummary]);

  const createRow = async () => {
    const text = draft.text.trim();
    if (!text || text.length < 3) {
      toast.error('Please add some text before saving.');
      return;
    }
    try {
      await axios.post(
        `${BACKEND}/api/admin/post-writer/training`,
        { kind: activeTab, text, label: draft.label.trim() || null, active: true },
        { headers: authHeaders(), timeout: 15000 },
      );
      setDraft({ text: '', label: '' });
      toast.success('Saved.');
      loadAll();
    } catch (e) {
      toast.error(
        e?.response?.data?.detail?.[0]?.msg ||
          e?.response?.data?.detail ||
          'Could not save.',
      );
    }
  };

  const patchRow = async (row, updates) => {
    try {
      const { data } = await axios.patch(
        `${BACKEND}/api/admin/post-writer/training/${row.id}`,
        updates,
        { headers: authHeaders(), timeout: 15000 },
      );
      const next = data?.item;
      if (!next) return;
      setRowsByKind((s) => ({
        ...s,
        [row.kind]: (s[row.kind] || []).map((r) => (r.id === next.id ? next : r)),
      }));
    } catch (e) {
      toast.error('Could not save.');
    }
  };

  const deleteRow = async (row) => {
    if (!window.confirm('Delete this training row?')) return;
    try {
      await axios.delete(`${BACKEND}/api/admin/post-writer/training/${row.id}`, {
        headers: authHeaders(),
      });
      setRowsByKind((s) => ({
        ...s,
        [row.kind]: (s[row.kind] || []).filter((r) => r.id !== row.id),
      }));
    } catch (e) {
      toast.error('Could not delete.');
    }
  };

  const saveEdit = async (row) => {
    if (editText.trim().length < 3) return;
    await patchRow(row, { text: editText.trim() });
    setEditingId(null);
    setEditText('');
  };

  const rows = rowsByKind[activeTab] || [];
  const meta = KIND_META[activeTab];

  return (
    <div className="space-y-6" data-testid="post-writer-trainer-panel">
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="h-10 rounded-xl">
          {Object.entries(KIND_META).map(([k, m]) => (
            <TabsTrigger
              key={k}
              value={k}
              className="h-8 gap-2 px-3"
              data-testid={m.testid}
            >
              <span>{m.label}</span>
              <Badge
                variant="secondary"
                className="rounded-full text-[10px] px-1.5 h-5 font-mono-edge"
                data-testid={`${m.testid}-count`}
              >
                {(rowsByKind[k] || []).length}
              </Badge>
            </TabsTrigger>
          ))}
        </TabsList>

        {Object.keys(KIND_META).map((k) => (
          <TabsContent key={k} value={k} className="mt-4 space-y-4">
            <p className="text-xs text-muted-foreground">{KIND_META[k].hint}</p>

            {/* Create form */}
            <Card className="border-border">
              <CardContent className="p-4 space-y-3">
                <div className="flex items-center gap-2">
                  <Sparkles size={14} className="text-primary" />
                  <span className="text-[11px] font-mono-edge tracking-[0.2em] uppercase text-muted-foreground">
                    Add new {KIND_META[k].label.toLowerCase()}
                  </span>
                </div>
                <Textarea
                  value={activeTab === k ? draft.text : ''}
                  onChange={(e) => setDraft((d) => ({ ...d, text: e.target.value }))}
                  placeholder={KIND_META[k].placeholder}
                  className="min-h-[100px]"
                  data-testid={`${KIND_META[k].testid}-new-text`}
                />
                <Input
                  value={activeTab === k ? draft.label : ''}
                  onChange={(e) => setDraft((d) => ({ ...d, label: e.target.value }))}
                  placeholder="Short label (optional) — e.g. 'Contrarian hook'"
                  data-testid={`${KIND_META[k].testid}-new-label`}
                />
                <div className="flex justify-end">
                  <Button
                    onClick={createRow}
                    disabled={loading}
                    size="sm"
                    className="rounded-full"
                    data-testid={`${KIND_META[k].testid}-save-btn`}
                  >
                    <Plus size={13} className="mr-1" /> Add to corpus
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Rows list */}
            <Card className="border-border">
              <CardContent className="p-0">
                <div className="p-3 flex items-center gap-2 border-b border-border">
                  <span className="text-[11px] font-mono-edge tracking-[0.2em] uppercase text-muted-foreground">
                    {KIND_META[k].label} · {rowsByKind[k]?.length || 0}
                  </span>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={loadAll}
                    className="ml-auto"
                    disabled={loading}
                  >
                    {loading ? (
                      <Loader2 size={13} className="animate-spin" />
                    ) : (
                      <RefreshCw size={13} />
                    )}
                  </Button>
                </div>
                {rowsByKind[k]?.length === 0 && !loading && (
                  <p className="p-6 text-sm text-muted-foreground text-center">
                    Nothing in this bucket yet.
                  </p>
                )}
                <ul className="divide-y divide-border">
                  {(rowsByKind[k] || []).map((row) => (
                    <li
                      key={row.id}
                      className="p-3 flex items-start gap-3"
                      data-testid={`${KIND_META[k].testid}-row-${row.id}`}
                    >
                      <Badge
                        variant="outline"
                        className={`shrink-0 text-[9px] tracking-wider uppercase font-mono-edge ${KIND_META[k].color}`}
                      >
                        {row.source === 'user_feedback' ? 'AUTO' : 'ADMIN'}
                      </Badge>
                      <div className="min-w-0 flex-1">
                        {editingId === row.id ? (
                          <Textarea
                            value={editText}
                            onChange={(e) => setEditText(e.target.value)}
                            className="min-h-[80px]"
                            data-testid={`${KIND_META[k].testid}-edit-input`}
                          />
                        ) : (
                          <p className="text-sm whitespace-pre-wrap leading-relaxed">
                            {row.text}
                          </p>
                        )}
                        <div className="flex items-center gap-2 mt-1 flex-wrap">
                          {row.label && (
                            <span className="text-[11px] text-muted-foreground">
                              {row.label}
                            </span>
                          )}
                          <Switch
                            checked={!!row.active}
                            onCheckedChange={(v) => patchRow(row, { active: v })}
                            data-testid={`${KIND_META[k].testid}-active-toggle-${row.id}`}
                          />
                          <span className="text-[10px] text-muted-foreground">
                            {row.active ? 'Active' : 'Paused'}
                          </span>
                        </div>
                      </div>
                      <div className="flex items-center gap-1 shrink-0">
                        {editingId === row.id ? (
                          <>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => saveEdit(row)}
                              data-testid={`${KIND_META[k].testid}-edit-save-${row.id}`}
                            >
                              <Check size={13} />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => {
                                setEditingId(null);
                                setEditText('');
                              }}
                            >
                              <X size={13} />
                            </Button>
                          </>
                        ) : (
                          <>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => {
                                setEditingId(row.id);
                                setEditText(row.text || '');
                              }}
                              data-testid={`${KIND_META[k].testid}-edit-${row.id}`}
                            >
                              <Edit3 size={13} />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => deleteRow(row)}
                              data-testid={`${KIND_META[k].testid}-delete-${row.id}`}
                            >
                              <Trash2 size={13} />
                            </Button>
                          </>
                        )}
                      </div>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          </TabsContent>
        ))}
      </Tabs>

      {/* Feedback digest */}
      <Card className="border-border" data-testid="post-writer-feedback-digest">
        <CardContent className="p-4">
          <div className="flex items-center gap-2 mb-3">
            <ThumbsUp size={14} className="text-emerald-600" />
            <span className="text-[11px] font-mono-edge tracking-[0.2em] uppercase text-muted-foreground">
              Live feedback · thumbs from buyers
            </span>
            <Button
              variant="ghost"
              size="sm"
              onClick={loadSummary}
              className="ml-auto"
            >
              <RefreshCw size={12} />
            </Button>
          </div>
          {!summary ? (
            <p className="text-xs text-muted-foreground">
              No feedback yet — buyers haven&apos;t rated posts in this window.
            </p>
          ) : (
            <div className="grid md:grid-cols-2 gap-3">
              <FeedbackCol
                title="Top-rated"
                icon={ThumbsUp}
                iconColor="text-emerald-600"
                items={summary.top_rated || []}
                countKey="up_count"
                testid="post-writer-feedback-top"
              />
              <FeedbackCol
                title="Bottom-rated"
                icon={ThumbsDown}
                iconColor="text-rose-600"
                items={summary.bottom_rated || []}
                countKey="down_count"
                testid="post-writer-feedback-bottom"
              />
            </div>
          )}
          <p className="text-[10.5px] text-muted-foreground mt-3">
            Posts with 2+ consistent thumbs are auto-added to the exemplar corpus above.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}

const FeedbackCol = ({ title, icon: Icon, iconColor, items, countKey, testid }) => (
  <div data-testid={testid}>
    <div className="flex items-center gap-1.5 mb-2">
      <Icon size={12} className={iconColor} />
      <span className="text-[11px] font-mono-edge tracking-[0.2em] uppercase text-muted-foreground">
        {title}
      </span>
    </div>
    {items.length === 0 ? (
      <p className="text-[11px] text-muted-foreground italic">Nothing here yet.</p>
    ) : (
      <ul className="space-y-2">
        {items.slice(0, 5).map((it) => (
          <li
            key={it.post_text_hash}
            className="rounded-lg border border-border bg-background p-2.5"
          >
            <div className="flex items-center gap-1.5 mb-1">
              <Badge variant="secondary" className="text-[9px] font-mono-edge">
                {it[countKey]}×
              </Badge>
            </div>
            <p className="text-[11.5px] leading-snug line-clamp-3">
              {it.post_text}
            </p>
            {it.sample_reason && (
              <p className="text-[10.5px] text-rose-500 mt-1 italic">
                “{it.sample_reason}”
              </p>
            )}
          </li>
        ))}
      </ul>
    )}
  </div>
);

export default PostWriterTrainerPanel;
